import React from "react";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import { useMutation } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";

import { pricingPlanService } from "../../services";
import { UpgradePricingPlanPayload } from "../../types";
import { useUserState } from "../../../../../store";
import { trackEvent } from "../../../../../analytics";

interface Props {
  pricingPlansData: Record<string, any>[];
}

const formatFeaturesFromAPI = (features: any[]): string[] => {
  if (!Array.isArray(features)) return [];
  return features.map((featureQuota) => {
    const { quota, feature } = featureQuota;
    const unitName = feature.unit_name || "unit";
    const unitNamePlural = quota === 1 ? unitName : `${unitName}s`;
    if (feature.name === "available_seats") return `${quota} ${unitNamePlural}`;
    if (feature.name === "number_of_custom_prompts_to_short_video_per_month")
      return `${quota} custom video prompts per month`;
    if (
      feature.name === "number_of_custom_prompts_to_textual_content_per_month"
    )
      return `${quota} custom content prompts per month`;
    return `${quota} ${feature.description || unitNamePlural}`;
  });
};

export default function PricingPlanCard({ pricingPlansData }: Props) {
  const theme = useTheme();
  const { mutateAsync, isSuccess, error, data, isPending } = useMutation({
    mutationKey: ["upgradePricingPlan"],
    mutationFn: (values: UpgradePricingPlanPayload) =>
      pricingPlanService.upgradePricingPlan(values),
  });
  const { user } = useUserState();

  const pricingPlansCount = pricingPlansData.length;

  const handlePricingPlanUpgrade = async (
    planId: string,
    recurringInterval: string,
    planName: string,
  ) => {
    trackEvent("plan_upgrade_clicked", {
      plan_id: planId,
      plan_name: planName,
      billing_cycle: recurringInterval,
    });
    const payload = {
      pricing_plan_id: planId,
      pricing_cycle: recurringInterval,
    };
    await toast.promise(mutateAsync(payload), {
      error: {
        toastId: "Error",
        render() {
          return `Error: ${JSON.stringify(error, null, 2)}`;
        },
        type: "error",
        theme: "colored",
        autoClose: false,
        transition: Bounce,
        delay: 500,
      },
    });
  };

  if (isSuccess) {
    window.location.replace(data.url);
  }

  if (isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={36} sx={{ color: "#2563eb" }} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {pricingPlansData?.map((pricingPlanData) => {
        const isCurrentPlan = !!(
          user?.organization?.subscription?.pricing_plan?.id &&
          user?.organization?.subscription?.pricing_plan?.id ===
            pricingPlanData.id
        );
        const isDisabled =
          pricingPlanData.is_allowed_to_purchase === false ||
          user?.organization?.subscription?.pricing_cycle === "yearly";
        const isPopular = !isCurrentPlan && pricingPlanData.is_popular;
        const features =
          Array.isArray(pricingPlanData.features) &&
          pricingPlanData.features.length > 0
            ? formatFeaturesFromAPI(pricingPlanData.features)
            : [];

        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={pricingPlansCount > 4 ? 4 : 3}
            key={pricingPlanData.id}
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                position: "relative",
                borderRadius: "12px",
                backgroundColor: "#fff",
                border: isPopular
                  ? "2px solid #2563eb"
                  : `2px solid ${theme.palette.grey[300]}`,
                boxShadow: isPopular
                  ? "0 8px 28px rgba(37, 99, 235, 0.2), 0 2px 6px rgba(37, 99, 235, 0.12)"
                  : "0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                height: "100%",
                minHeight: { xs: 400, sm: 440, md: 460 },
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                opacity: isDisabled ? 0.6 : 1,
                "&:hover": {
                  borderColor: isPopular ? "#1d4ed8" : "#2563eb",
                  boxShadow: isPopular
                    ? "0 12px 36px rgba(37, 99, 235, 0.25), 0 4px 10px rgba(37, 99, 235, 0.15)"
                    : "0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(37, 99, 235, 0.12)",
                },
              }}
            >
              {/* Popular badge */}
              {isPopular && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -13,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    borderRadius: "6px",
                    border: "2px solid #fff",
                    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.35)",
                    px: 1.5,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  <StarIcon sx={{ fontSize: 13, color: "#fbbf24" }} />
                  Most Popular
                </Box>
              )}

              <Box
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "100%",
                  minWidth: 0,
                }}
              >
                {/* Plan name + description */}
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "text.primary",
                    mb: 0.5,
                    minWidth: 0,
                    width: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    hyphens: "auto",
                  }}
                >
                  {pricingPlanData.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.secondary",
                    lineHeight: 1.5,
                    mb: 1.5,
                    minWidth: 0,
                    width: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {pricingPlanData.description}
                </Typography>

                {pricingPlanData.annual_billing_summary && (
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "#16a34a",
                      lineHeight: 1.45,
                      mb: 1.5,
                    }}
                  >
                    {pricingPlanData.annual_billing_summary}
                  </Typography>
                )}

                {/* Price — yearly shows monthly equivalent + annual line above */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mb: 2,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "2rem",
                      fontWeight: 800,
                      color: "text.primary",
                      lineHeight: 1,
                      letterSpacing: "-0.03em",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {pricingPlanData.price_highlight ||
                      pricingPlanData.price_in_cents ||
                      "Free"}
                  </Typography>
                  {(pricingPlanData.price_highlight ||
                    pricingPlanData.price_in_cents) && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.9rem",
                        color: "text.secondary",
                        fontWeight: 500,
                        alignSelf: "flex-end",
                        pb: 0.35,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {pricingPlanData.price_period_label || "/month"}
                    </Typography>
                  )}
                </Box>

                {/* Credits */}
                {pricingPlanData.credits_per_month && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      alignSelf: "flex-start",
                      maxWidth: "100%",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "6px",
                      px: 1,
                      py: 0.25,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#2563eb",
                        lineHeight: 1.4,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {pricingPlanData.credits_per_month.toLocaleString()}{" "}
                      credits/month
                    </Typography>
                  </Box>
                )}

                {/* CTA button */}
                <Button
                  variant={isCurrentPlan ? "outlined" : "contained"}
                  fullWidth
                  onClick={() =>
                    handlePricingPlanUpgrade(
                      pricingPlanData.id,
                      pricingPlanData.recurring_interval,
                      pricingPlanData.name,
                    )
                  }
                  disabled={isDisabled || isCurrentPlan}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    py: 1,
                    mb: 3,
                    backgroundColor: isCurrentPlan ? "transparent" : "#2563eb",
                    borderColor: isCurrentPlan ? "divider" : undefined,
                    color: isCurrentPlan ? "text.secondary" : "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: isCurrentPlan
                        ? "action.hover"
                        : "#1d4ed8",
                      boxShadow: isCurrentPlan
                        ? "none"
                        : "0 2px 8px rgba(37,99,235,0.25)",
                    },
                    "&:disabled": {
                      backgroundColor: "action.disabledBackground",
                      color: "text.disabled",
                    },
                  }}
                >
                  {isCurrentPlan ? "✓ Current Plan" : "Get Started"}
                </Button>

                {/* Divider — grows to fill card when minHeight set */}
                {features.length > 0 && (
                  <Box
                    sx={{
                      borderTop: `2px solid ${theme.palette.grey[200]}`,
                      pt: 2.5,
                      mt: "auto",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 1.5,
                      }}
                    >
                      Includes
                    </Typography>
                    {features.map((feature: string) => (
                      <Box
                        key={feature}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1,
                          minWidth: 0,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 16,
                            color: "#10b981",
                            flexShrink: 0,
                            mt: "2px",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            lineHeight: 1.5,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
